import React, { useState, useEffect } from 'react';
import { Loader2, RotateCcw, Check, X } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { _money, _FREQ_CHOICES } from './utils';
import { VarianceCell } from './VarianceCell';
import { BudgetSiteEditDialog } from '../../components/BudgetSiteEditDialog';

// ── MonthCell (iter495s + iter495t) ───────────────────────────────
// Double-click a month cell to enter a manual £ that replaces the
// projected value. Once open the user can Tab across the row to
// edit consecutive months without double-clicking each cell —
// editing state is lifted to the parent row so a single "active
// column" flows left/right across the year. Save on Tab / Enter /
// blur; Escape reverts. A cleared value deletes the override.
function MonthCell({ siteId, ym, projected, override, isEditing, onStartEdit, onFinishEdit, onDataChanged }) {
  const [value, setValue] = useState(override != null ? String(override) : '');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  useEffect(() => { setValue(override != null ? String(override) : ''); }, [override, isEditing]);
  const commit = async (nextDirection) => {
    const raw = value.trim();
    const priceNumOrNull = raw === '' ? null : Number(raw);
    if (priceNumOrNull !== null && (!Number.isFinite(priceNumOrNull) || priceNumOrNull < 0)) {
      toast({ title: 'Invalid £', description: 'Enter a non-negative number or leave blank to clear', variant: 'destructive' });
      onFinishEdit && onFinishEdit(null); return;
    }
    const currentOv = override == null ? null : Number(override);
    if (currentOv === priceNumOrNull) {
      onFinishEdit && onFinishEdit(nextDirection);
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/reports/revenue-budget/month-overrides/${siteId}`, {
        ym, price: priceNumOrNull,
      });
      onDataChanged && onDataChanged();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSaving(false);
      onFinishEdit && onFinishEdit(nextDirection);
    }
  };
  const cellClass = `px-2 py-1.5 text-right border-b select-none ${
    override != null ? 'bg-indigo-50/70 font-semibold text-indigo-900 border-l-2 border-indigo-400'
    : (projected ? 'hover:bg-slate-50' : 'text-slate-300 hover:bg-slate-50')
  }`;
  if (isEditing) {
    return (
      <td className={cellClass} data-testid={`month-cell-${siteId}-${ym}`}>
        <input
          type="number"
          step="0.01"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => commit(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(null); }
            else if (e.key === 'Tab') {
              e.preventDefault();
              commit(e.shiftKey ? 'prev' : 'next');
            }
            else if (e.key === 'Escape') {
              e.preventDefault();
              setValue(override != null ? String(override) : '');
              onFinishEdit && onFinishEdit(null);
            }
          }}
          className="w-16 text-right text-[11px] border border-indigo-400 rounded px-1 py-0.5 bg-white"
          data-testid={`month-input-${siteId}-${ym}`}
          disabled={saving}
        />
      </td>
    );
  }
  return (
    <td
      className={cellClass}
      onDoubleClick={() => onStartEdit && onStartEdit()}
      title={override != null
        ? `Manual override £${override} — double-click to edit, Tab to move across months`
        : 'Double-click to set a manual monthly £ override. Tab to fill more months.'}
      data-testid={`month-cell-${siteId}-${ym}`}
      data-override={override != null ? 'true' : 'false'}
    >
      {override != null ? _money(override) : (projected ? _money(projected) : '—')}
    </td>
  );
}

// ── EditableSiteRow (iter483) ─────────────────────────────────────
// Inline editing: Service / Frequency / £/job / Start-date are edited
// directly in the grid cells. When the row is dirty, an action strip
// appears in the FAR LEFT cell with Save / Discard / Save to Master.
// The Save-to-Master button uses a two-click confirmation instead of
// a native window.confirm so we never open a modal from the grid.
export function EditableSiteRow({ site, months, showLastYear, onDataChanged, dateRange, previewUplift, allowSiteDiscontinue, pendingDiscontinueSiteIds, onToggleSiteDiscontinue, onDirtyChange, onSiteNameRefresh }) {
  const { toast } = useToast();

  const initial = React.useMemo(() => ({
    freq: site.frequency_override ?? '',
    customDays: site.custom_frequency_days ?? '',
    price: site.price_override != null ? String(site.price_override) : '',
    start: site.start_date_override || '',
    scheduleStart: site.budget_schedule_start_override || '',
    service: site.service_id_override ? String(site.service_id_override) : '',
    notes: site.notes || '',
  }), [site]);

  const [state, setState] = useState(initial);
  const [saving, setSaving] = useState(false);
  // iter495t — Which month cell (if any) is currently in edit mode.
  // Lifted to the row so Tab / Shift+Tab can flow left/right across
  // the year without requiring the user to double-click each cell.
  const [editingYm, setEditingYm] = useState(null);
  // Reset local state whenever the row from the server changes.
  useEffect(() => { setState(initial); }, [initial]);

  const dirty = (
    state.freq !== initial.freq
    || String(state.customDays) !== String(initial.customDays)
    || state.price !== initial.price
    || state.start !== initial.start
    || state.scheduleStart !== initial.scheduleStart
    || state.service !== initial.service
    || state.notes !== initial.notes
  );

  // iter495r — REPLACED the 800ms debounced auto-save with a row-blur
  // + explicit-Save flow. The debounce was firing mid-typing and moved
  // rows Section 2 → 1 before the entry was complete. Now:
  //   • Nothing writes while the user is inside the row's inputs.
  //   • Save fires ONCE when focus leaves the whole row (any input's
  //     blur whose relatedTarget is outside the row wrapper).
  //   • The explicit ✓ Save button still forces an immediate write.
  //   • Parent gets a `onDirtyChange(siteId, dirty)` signal so it can
  //     nag on Publish / navigation with pending edits.
  const saveRef = React.useRef(null);
  useEffect(() => {
    if (typeof onDirtyChange === 'function') onDirtyChange(site.site_id, dirty);
  }, [dirty, site.site_id, onDirtyChange]);
  useEffect(() => () => {
    // Clear the dirty signal when the row unmounts (e.g. filter change).
    if (typeof onDirtyChange === 'function') onDirtyChange(site.site_id, false);
  }, [site.site_id, onDirtyChange]);

  const hasOverride = !!(site.frequency_override || site.price_override != null
    || site.start_date_override || site.service_id_override || site.notes);

  const save = async () => {
    setSaving(true);
    try {
      const freqValue = state.freq || null;
      const svcValue = state.service ? Number(state.service) : null;
      await api.patch(`/reports/revenue-budget/site-overrides/${site.site_id}`, {
        frequency_override: freqValue,
        custom_days_override: freqValue === 'custom' && state.customDays ? Number(state.customDays) : null,
        price_override: state.price !== '' ? Number(state.price) : null,
        start_date_override: state.start || null,
        budget_schedule_start_override: state.scheduleStart || null,
        service_id_override: svcValue,
        notes: state.notes || null,
      });
      // iter495r — Silent write is intentional for the row-blur path
      // (Save is triggered by the user leaving the row, not by every
      // keystroke). The explicit Save button surfaces a toast so the
      // user gets confirmation when they actively click Save.
      onDataChanged && onDataChanged();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  saveRef.current = save;

  // iter495r — Row-blur commit: when focus genuinely leaves the row,
  // flush any pending edits. `relatedTarget` is null when the user
  // clicks outside all focusables (e.g. a table row); we only skip if
  // focus stayed within this row's inputs/buttons.
  const handleRowBlur = (e) => {
    if (!dirty || saving) return;
    if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    save();
  };

  const explicitSave = async () => {
    await save();
    toast({ title: 'Saved', description: site.site_name });
  };

  const clearOverride = async () => {
    setSaving(true);
    try {
      await api.patch(`/reports/revenue-budget/site-overrides/${site.site_id}`, {
        frequency_override: null, custom_days_override: null,
        price_override: null, start_date_override: null,
        budget_schedule_start_override: null,
        service_id_override: null, notes: null,
      });
      toast({ title: 'Cleared', description: site.site_name });
      onDataChanged && onDataChanged();
    } catch (err) {
      toast({ title: 'Clear failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // iter495s — Single-row master push has been removed. All master
  // writes must now go through the bulk Review & Publish flow so the
  // user always sees a full-summary confirmation before touching the
  // customer master.

  const serviceOptions = site.service_options || [];
  const showActions = dirty || hasOverride;
  // iter488 — Preview highlight: is this site affected by the hovered/toggled uplift?
  const previewMatches = !!previewUplift && (
    (previewUplift.scope === 'global' && !previewUplift.excludeClientIds?.has(site.client_id))
    || (previewUplift.scope === 'client' && previewUplift.client_id === site.client_id)
  );
  // iter495r — Amber left border when the row has unsaved edits so
  // schedulers see instantly which rows will be flushed on Publish.
  const dirtyClass = dirty ? 'ring-2 ring-inset ring-amber-300 bg-amber-50/40' : '';
  const rowClass = `${previewMatches ? 'bg-emerald-50/70' : 'hover:bg-slate-50/60'} ${dirtyClass}`.trim();

  return (
    <tr
      className={rowClass}
      onBlur={handleRowBlur}
      data-testid={`editable-site-row-${site.site_id}`}
      data-dirty={dirty ? 'true' : 'false'}
    >
      {/* LEFT actions cell (iter483) — inline Save / Discard / Master */}
      <td className="px-1 py-1 border-b sticky left-0 bg-white z-10 whitespace-nowrap" data-testid={`row-actions-${site.site_id}`}>
        {allowSiteDiscontinue && (
          <div className="mb-1 flex justify-center">
            <button
              type="button"
              onClick={() => onToggleSiteDiscontinue && onToggleSiteDiscontinue(site.site_id)}
              className={
                'text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap font-semibold ' +
                (pendingDiscontinueSiteIds?.has?.(site.site_id)
                  ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700'
                  : 'bg-white text-rose-700 border-rose-300 hover:bg-rose-50')
              }
              data-testid={`discontinue-site-toggle-${site.site_id}`}
              title={pendingDiscontinueSiteIds?.has?.(site.site_id)
                ? 'Marked Inactive — click to undo'
                : 'Mark site as Inactive (Customer Master)'}
            >
              {pendingDiscontinueSiteIds?.has?.(site.site_id) ? '✓ Inactive' : 'Set Inactive'}
            </button>
          </div>
        )}
        {showActions ? (
          <div className="flex items-center gap-0.5 justify-center">
            {dirty && (
              <>
                <button
                  type="button"
                  onClick={explicitSave}
                  disabled={saving}
                  className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
                  title="Save override"
                  data-testid={`save-site-${site.site_id}`}
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => setState(initial)}
                  disabled={saving}
                  className="p-1 rounded hover:bg-slate-100 text-slate-500"
                  title="Discard changes"
                  data-testid={`discard-site-${site.site_id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </>
            )}
            {hasOverride && !dirty && (
              <button
                type="button"
                onClick={clearOverride}
                disabled={saving}
                className="p-1 rounded hover:bg-rose-100 text-rose-600"
                title="Clear override"
                data-testid={`clear-site-${site.site_id}`}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
            {/* iter495s — Per-row Master push removed. Use Review & Publish. */}
          </div>
        ) : null}
      </td>

      <td className="px-2 py-1.5 border-b sticky left-24 bg-white z-10 whitespace-nowrap pl-3 group" data-testid={`site-row-${site.site_id}`}>
        <span className="flex items-center gap-0.5">
          <span className="truncate max-w-[180px]" title={site.site_name}>{site.site_name}</span>
          {/* Budget Site Edit shortcut — opens the same dialog as Customers page */}
          <BudgetSiteEditDialog
            siteId={site.site_id}
            clientId={site.client_id}
            isIndependent={!!site.is_independent}
            siteName={site.site_name}
            onSaved={(updated) => {
              // Refresh only the site name in this row without touching budget state
              if (updated?.name && onSiteNameRefresh) {
                onSiteNameRefresh(site.site_id, updated.name);
              }
            }}
          />
        </span>
        {dirty && (
          <span className="ml-2 text-[10px] text-amber-700 font-bold uppercase animate-pulse" title="Unsaved changes — will save when you leave the row or click Save">unsaved</span>
        )}
        {!dirty && hasOverride && (
          <span className="ml-2 text-[10px] text-amber-600 font-semibold uppercase" title="Values overridden">edited</span>
        )}
        {site.has_month_overrides && (
          <span
            className="ml-2 text-[10px] text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 font-semibold uppercase tracking-tight"
            title="This site has manual per-month £ overrides — master push still applies."
            data-testid={`month-locked-${site.site_id}`}
          >
            manual edits
          </span>
        )}
      </td>
      <td className="px-2 py-1.5 border-b whitespace-nowrap">{site.last_audit_date || '—'}</td>

      {/* Service — inline picker; wraps to 2 lines if needed */}
      <td className="px-1 py-1 border-b align-top">
        <select
          value={state.service}
          onChange={(e) => setState(s => ({ ...s, service: e.target.value }))}
          className="text-[11px] border rounded px-1 py-0.5 w-full bg-transparent whitespace-normal leading-tight"
          data-testid={`inline-service-${site.site_id}`}
          style={{ minHeight: '1.65rem' }}
          title={site.primary_service_name || ''}
        >
          <option value="">{site.primary_service_name ? `${site.primary_service_name} · master` : '— use master —'}</option>
          {serviceOptions.map(o => (
            <option key={o.service_id} value={String(o.service_id)}>
              {o.service_name}{o.is_unpriced ? ' — new · set £ on publish' : ` (£${o.price})`}
            </option>
          ))}
        </select>
      </td>

      {/* Frequency — inline picker. iter485: default option shows the site's
          MASTER frequency instead of a generic "use master / clear" label. */}
      <td className="px-1 py-1 border-b whitespace-nowrap">
        <select
          value={state.freq}
          onChange={(e) => setState(s => ({ ...s, freq: e.target.value }))}
          className="text-[11px] border rounded px-1 py-0.5 w-full bg-transparent capitalize"
          data-testid={`inline-frequency-${site.site_id}`}
        >
          <option value="">
            {(site.frequency ? `${site.frequency}${site.custom_frequency_days ? ` (${site.custom_frequency_days}d)` : ''}` : '—')} · master
          </option>
          {_FREQ_CHOICES.filter(c => c.v !== '' && c.v !== '__none__').map(c => (
            <option key={c.v} value={c.v}>{c.label}</option>
          ))}
        </select>
        {state.freq === 'custom' && (
          <input
            type="number"
            value={state.customDays}
            onChange={(e) => setState(s => ({ ...s, customDays: e.target.value }))}
            placeholder="days"
            className="text-[11px] border rounded px-1 py-0.5 w-16 mt-1"
            data-testid={`inline-custom-days-${site.site_id}`}
          />
        )}
      </td>

      {/* Price effective from (renamed from "Start override") */}
      <td className="px-1 py-1 border-b whitespace-nowrap">
        <input
          type="date"
          value={state.start || dateRange?.start || ''}
          min={dateRange?.start}
          max={dateRange?.end}
          onChange={(e) => setState(s => ({ ...s, start: e.target.value }))}
          className="text-[11px] border rounded px-1 py-0.5 w-28 bg-transparent"
          data-testid={`inline-start-${site.site_id}`}
          title={`Price effective from — Budget range ${dateRange?.start} → ${dateRange?.end}`}
        />
      </td>

      {/* Budget schedule start override — cadence anchor for first projected job */}
      <td className="px-1 py-1 border-b whitespace-nowrap">
        <input
          type="date"
          value={state.scheduleStart || ''}
          min={dateRange?.start}
          max={dateRange?.end}
          onChange={(e) => setState(s => ({ ...s, scheduleStart: e.target.value }))}
          className="text-[11px] border rounded px-1 py-0.5 w-28 bg-transparent"
          data-testid={`inline-schedule-start-${site.site_id}`}
          title="Schedule start override — anchors the first projected audit date; subsequent audits follow normal cadence"
        />
      </td>

      <td className="px-2 py-1.5 text-right border-b">{_money(site.last_billed_amount)}</td>

      {/* £/job — with iter488 effective-date badge for upcoming Master change */}
      <td className="px-1 py-1 border-b text-right">
        <input
          type="number"
          step="0.01"
          value={state.price}
          onChange={(e) => setState(s => ({ ...s, price: e.target.value }))}
          placeholder={String(site.forecast_price ?? 0)}
          className="text-[11px] border rounded px-1 py-0.5 w-16 text-right bg-transparent"
          data-testid={`inline-price-${site.site_id}`}
        />
        {site.upcoming_price_change && (
          <div
            className="text-[9px] text-amber-700 mt-0.5 leading-none whitespace-nowrap"
            title={`Master changes to £${site.upcoming_price_change.price} on ${site.upcoming_price_change.effective_from}`}
            data-testid={`upcoming-price-${site.site_id}`}
          >
            £{Math.round(site.upcoming_price_change.price)} from {String(site.upcoming_price_change.effective_from).slice(5)}
          </div>
        )}
      </td>

      {/* Notes (iter485) — free-text assumption per row. */}
      <td className="px-1 py-1 border-b">
        <input
          type="text"
          value={state.notes}
          onChange={(e) => setState(s => ({ ...s, notes: e.target.value }))}
          placeholder="—"
          className="text-[11px] border rounded px-1 py-0.5 w-full bg-transparent"
          data-testid={`inline-notes-${site.site_id}`}
          title="Notes / assumptions"
        />
      </td>

      {months.map(m => {
        const monthOv = site.month_overrides && site.month_overrides[m];
        return (
          <MonthCell
            key={m}
            siteId={site.site_id}
            ym={m}
            projected={site.months?.[m]}
            override={monthOv}
            isEditing={editingYm === m}
            onStartEdit={() => setEditingYm(m)}
            onFinishEdit={(direction) => {
              if (direction === 'next' || direction === 'prev') {
                const idx = months.indexOf(m);
                const nextIdx = direction === 'next' ? idx + 1 : idx - 1;
                if (nextIdx >= 0 && nextIdx < months.length) {
                  setEditingYm(months[nextIdx]);
                  return;
                }
              }
              setEditingYm(null);
            }}
            onDataChanged={onDataChanged}
          />
        );
      })}
      <td className="px-2 py-1.5 text-right font-medium border-l border-b bg-slate-50/40">{_money(site.total)}</td>
      {showLastYear && <>
        <td className="px-2 py-1.5 text-right border-b">{_money(site.last_year_total)}</td>
        <td className="px-2 py-1.5 text-right border-b"><VarianceCell variance={site.variance} /></td>
      </>}
    </tr>
  );
}
